import type {
  Mutation,
  MutationCacheNotifyEvent,
  MutationFunctionContext,
  QueryClient,
} from "@tanstack/react-query";
import type { DashboardOverview, User } from "@/api/types";
import { useToastStore } from "@/components/ui/toast";
import { resetPosStore } from "@/features/dashboard/pos/use-pos-store";
import { clearBusinessId } from "@/lib/onboarding-storage";
import {
  assertCurrentSession,
  notifySessionChange,
  replaceSessionRequestScope,
  sessionRevision,
} from "@/lib/session-request-scope";
import { useAuthUIStore } from "@/stores/auth-ui";

const identities = new WeakMap<QueryClient, string | null>();
const workspaces = new WeakMap<QueryClient, string>();
type SessionMutation = Mutation<unknown, unknown, unknown, unknown>;
const mutationOwners = new WeakMap<MutationFunctionContext, SessionMutation>();
const mutationRevisions = new WeakMap<object, number>();
const mutationObservers = new WeakMap<object, Set<{ reset: () => void }>>();
const pendingMutations = new WeakMap<QueryClient, Set<SessionMutation>>();

export function recordSessionMutation(
  context: MutationFunctionContext,
  mutation: SessionMutation,
): void {
  mutationRevisions.set(mutation, sessionRevision());
  mutationOwners.set(context, mutation);
  const pending = pendingMutations.get(context.client) ?? new Set<SessionMutation>();
  pending.add(mutation);
  pendingMutations.set(context.client, pending);
}

export function isCurrentSessionMutation(mutation: object): boolean {
  return mutationRevisions.get(mutation) === sessionRevision();
}

export function assertCurrentSessionMutation(mutation: object): void {
  assertCurrentSession(mutationRevisions.get(mutation));
}

export function trackSessionMutations(
  queryClient: QueryClient,
  event: MutationCacheNotifyEvent,
): void {
  if (event.type === "observerAdded") {
    const observers = mutationObservers.get(event.mutation) ?? new Set<{ reset: () => void }>();
    observers.add(event.observer);
    mutationObservers.set(event.mutation, observers);
  } else if (event.type === "observerRemoved") {
    mutationObservers.get(event.mutation)?.delete(event.observer);
  } else if (
    event.type === "updated" &&
    (event.action.type === "success" || event.action.type === "error")
  ) {
    pendingMutations.get(queryClient)?.delete(event.mutation);
  }
}

export function retireSessionMutation<TError>(
  mutation: Mutation<unknown, TError, unknown, unknown>,
): void {
  mutation.setOptions({
    ...mutation.options,
    mutationFn: () =>
      Promise.reject(
        new DOMException("The account or business changed. Please try again.", "AbortError"),
      ),
    onMutate: undefined,
    onSuccess: undefined,
    onError: undefined,
    onSettled: undefined,
  });
  for (const observer of mutationObservers.get(mutation) ?? []) observer.reset();
}

/** Retire requests before clearing caches so late responses cannot restore private data. */
export function transitionSession(
  queryClient: QueryClient,
  user?: User | null,
  origin?: MutationFunctionContext,
): void {
  replaceSessionRequestScope();
  const owner = origin && mutationOwners.get(origin);
  if (owner) mutationRevisions.set(owner, sessionRevision());
  // TanStack awaits cache callbacks before invoking hook callbacks. Retire
  // synchronously, including mutations whose cache revision check just passed.
  // Keep owners in scope until their final update: clear() removes them from
  // TanStack's cache before their settled and per-call callbacks have finished.
  const pending = pendingMutations.get(queryClient);
  for (const mutation of pending ?? []) {
    if (mutation.state.status === "pending" && mutation !== owner) {
      retireSessionMutation(mutation);
      pending?.delete(mutation);
    }
  }
  void queryClient.cancelQueries();
  queryClient.clear();
  clearBusinessId();
  resetPosStore();
  useAuthUIStore.getState().setRedirect(null);
  useToastStore.setState({ toasts: [] });
  workspaces.delete(queryClient);
  if (user === undefined) identities.delete(queryClient);
  else identities.set(queryClient, user?.id ?? null);
  if (user !== undefined) queryClient.setQueryData(["me"], user);
  notifySessionChange(queryClient);
}

export function observeSessionUser(queryClient: QueryClient, user: User | null): void {
  const previous = identities.get(queryClient);
  const next = user?.id ?? null;
  if (previous !== undefined && previous !== next) {
    transitionSession(queryClient, user);
  } else {
    identities.set(queryClient, next);
  }
}

export function enterDashboardContext(queryClient: QueryClient, slug: string): void {
  const previous = workspaces.get(queryClient);
  if (previous !== undefined && previous !== slug) {
    transitionSession(queryClient, queryClient.getQueryData<User | null>(["me"]));
  }
  workspaces.set(queryClient, slug);
}

/** A renamed route still belongs to the same workspace and request scope. */
export function renameDashboardContext(
  queryClient: QueryClient,
  {
    previousSlug,
    slug,
    businessId,
    revision,
  }: { previousSlug: string; slug: string; businessId: string; revision: number },
): void {
  const overview = queryClient.getQueryData<DashboardOverview>([
    "dashboard",
    previousSlug,
    "overview",
  ]);
  if (
    sessionRevision() === revision &&
    workspaces.get(queryClient) === previousSlug &&
    overview?.business.id === businessId
  ) {
    workspaces.set(queryClient, slug);
  }
}
